import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { initializeAdsTrackers, trackConversionEvent } from '@/lib/adsTracking';
import { configSettings } from '@/lib/config';

interface AdsTrackingContextValue {
    trackEvent: (event: string, data?: Record<string, any>, serverData?: Record<string, any>) => Promise<void>;
}

const AdsTrackingContext = createContext<AdsTrackingContextValue | null>(null);

/**
 * Provider that initializes ad tracking pixels (Facebook, TikTok, LinkedIn) and provides tracking functions
 */
export function AdsTrackingProvider({ children }: { children: React.ReactNode }) {
    // Initialize trackers on mount (production only)
    useEffect(() => {
        if (configSettings.environment === 'production') {
            initializeAdsTrackers();
        }
    }, []);

    const trackEvent = useCallback(
        async (event: string, data: Record<string, any> = {}, serverData: Record<string, any> = {}) => {
            if (configSettings.environment !== 'production') {
                // eslint-disable-next-line no-console
                console.log('[Ads Tracking - Dev Mode]', { event, data, serverData });
                return;
            }

            try {
                await trackConversionEvent(event, data, serverData);
            } catch (error) {
                console.error('Failed to track conversion event:', error);
            }
        },
        []
    );

    const value = useMemo<AdsTrackingContextValue>(
        () => ({
            trackEvent,
        }),
        [trackEvent]
    );

    return <AdsTrackingContext.Provider value={value}>{children}</AdsTrackingContext.Provider>;
}

/**
 * Hook to access ad tracking functions
 * Must be used within AdsTrackingProvider
 */
export function useAdsTracking() {
    const context = useContext(AdsTrackingContext);
    if (!context) {
        throw new Error('useAdsTracking must be used within AdsTrackingProvider');
    }
    return context;
}
